import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { EnumType } from 'json-to-graphql-query'
import classNames from 'classnames'

import AskVisoActions from './AskVisoActions'
import { Select } from 'containers/CustomDashboard/Dashboard/details/editor/inputs/Select'
import { TextInput } from 'containers/CustomDashboard/Dashboard/details/editor/inputs/TextInput'
import { defaultNotifier } from 'functions/notificationHandler'
import { TIME_WINDOW_OPTIONS, UNITS_OPTIONS, CURRENCY_OPTIONS, DECIMALS_OPTIONS, PERSONA_OPTIONS, TONE_OPTIONS, FORMATTING_OPTIONS, STRATEGY_OPTIONS, VERBOSITY_OPTIONS, OPTIMIZATION_GOAL_OPTIONS, CONSTRAINTS_OPTIONS, MAX_ADDED_NOTES } from '../../constants'
import { updateAgentSettings } from 'api/ai-agents'
import { handleMutation } from 'utils/request'
import '../content/AskVisoSettings.scss'

const AskVisoSettings = ({ color, locationsData, devicesData, settingsData, setSettingsData, setActiveTab, isFullscreen }) => {
  const { dataContext, responseStyle, id, locations, technical, system } = settingsData;
  const { timeWindow, devices = [] } = dataContext
  const [settingsLoading, toggleSettingsLoading] = useState(false)
  const [isTouched, toggleIsTouched] = useState(false);

  const [formValues, setFormValues] = useState({
    responseStyle: {
      persona : responseStyle?.persona,
      strategy : responseStyle?.strategy,
      verbosity : responseStyle?.verbosity,
    },
    technical: {
      optimization : technical?.optimization || '',
      constraints : technical?.constraints || '',
      maxNodes : technical?.maxNodes
    },
    system: {
      instructions: system?.instructions || ''
    }
  })

  const handleFormatSelection = (value) => {
    toggleIsTouched(true)
    if (value.value === 'default') {
      setFormValues({ ...formValues, formatting: {} })
    } else {
      setFormValues({
        ...formValues,
        formatting: {
          unit: formatting.unit || 'metric',
          currency: formatting.currency || 'USD',
          decimals: formatting.decimals || 'none'
        }
      })
    }
  }

  const handleChange = (value) => {
    toggleIsTouched(true)
    if (Object.prototype.hasOwnProperty.call(value, 'timeWindow')) {
      const tw = value.timeWindow
      let normalized = tw
      const hasStartEnd = tw && tw.start && tw.end
      const looksRelative = !!(tw && ((tw.label && (/\blast\b/i.test(tw.label) || /^all$/i.test(tw.label))) || tw.apiValue === 'none'))
      if (hasStartEnd && !looksRelative) {
        setFormValues({ ...formValues, timeWindow: { ...tw, type: 'absolute' } })
        return
      }
      if (!tw?.apiValue) {
        if (tw?.value) {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.apiValue === tw.value) || { ...tw, apiValue: tw.value, label: tw.label || '' }
        } else if (tw?.label) {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.label === tw.label) || tw
        } else if (typeof tw?.totalDays !== 'undefined') {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.totalDays === tw.totalDays) || tw
        }
      }
      const relativeSelected = normalized?.apiValue ? { ...normalized, type: 'relative', value: normalized.apiValue } : { ...normalized, type: 'relative' }
      setFormValues({ ...formValues, timeWindow: relativeSelected })
      return
    }
    setFormValues({ ...formValues, ...value })
  }

  const handleSubmit = async () => {
    toggleSettingsLoading(true)
    const payload = {
      ...formValues
    }

    try {
      const onSuccess = (response) => {
        setSettingsData(response.data.updateAgentSettings)
      }
      const response = await updateAgentSettings({ id: id, ...payload })
      handleMutation(response, onSuccess)
      defaultNotifier('Settings updated successfully')
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleSettingsLoading(false)
    }
  }

  return (
    <div className={classNames('ask-viso-settings', isFullscreen && 'ask-viso-settings--fullscreen')}>

      <div className='ask-viso-settings__title'>Response Style</div>
        <Select
        label='Persona'
        selectedOption={PERSONA_OPTIONS.find(option => option.value === formValues.responseStyle.persona || 'builder')}
        options={PERSONA_OPTIONS}
        onChange={(value) => handleChange({ responseStyle: { ...formValues.responseStyle, persona: value.value } })}
      />

      <Select
        label='Strategy'
        selectedOption={STRATEGY_OPTIONS.find(option => option.value === formValues.responseStyle.strategy || 'suggestAndConfirm')}
        options={STRATEGY_OPTIONS}
        onChange={(value) => handleChange({ responseStyle: { ...formValues.responseStyle, strategy: value.value } })}
      />


      <Select
        label='Verbosity'
        selectedOption={VERBOSITY_OPTIONS.find(option => option.value === formValues.responseStyle.verbosity || 'brief')}
        options={VERBOSITY_OPTIONS}
        onChange={(value) => handleChange({ responseStyle: { ...formValues.responseStyle, verbosity: value.value } })}
      />

      <div className='ask-viso-settings__title'>Technical</div>


      <Select
        label='Optimization goal'
        selectedOption={OPTIMIZATION_GOAL_OPTIONS.find(option => option.value === formValues?.technical?.optimization || 'balanced')}
        options={OPTIMIZATION_GOAL_OPTIONS}
        onChange={(value) => handleChange({ technical: { ...formValues?.technical, optimization: value.value } })}
      />

      <Select
        label='Constraints'
        selectedOption={CONSTRAINTS_OPTIONS.find(option => option.value === formValues.technical.constraints || 'none')}
        options={CONSTRAINTS_OPTIONS}
        onChange={(value) => handleChange({ technical: { ...formValues?.technical, constraints: value.value } })}
      />

      <Select
        label='Max. added nodes'
        selectedOption={MAX_ADDED_NOTES.find(option => option.value === formValues.technical.maxNodes || '10')}
        options={MAX_ADDED_NOTES}
        onChange={(value) => handleChange({ technical: { ...formValues?.technical, maxNodes: value.value } })}
      />

      <div className='ask-viso-settings__title'>System</div>

      <TextInput
        value={formValues.system.instructions}
        color={color}
        onChange={(value) => handleChange({ system: { ...formValues.system, instructions: value } })}
        options={locationsData}
        label='Instructions'
        rectangularTags
        borderInput
        showAllOption='All'
      />
            <div className='ask-viso-settings__actions'>
        <AskVisoActions
          color={color}
          onSubmit={handleSubmit}
          submitLabel='Save'
          onCancel={() => setActiveTab('welcome')}
          submitDisabled={!isTouched || settingsLoading}
          cancelDisabled={!isTouched || settingsLoading}
        />
      </div>
    </div>
  )
}

export default AskVisoSettings
